import React from "react";
import { motion } from "framer-motion";
import { Button } from "./components/ui/button";

const LandingPage = () => {
  return (
    <div className="min-h-screen bg-slate-950 text-white font-sans">
      {/* Hero Section */}
      <section className="flex flex-col items-center justify-center text-center py-24 px-6">
        <motion.h1
          className="text-4xl md:text-6xl font-bold mb-6 text-transparent bg-clip-text bg-gradient-to-r from-white to-blue-400"
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        >
          Intent-Aware Security for the Modern Web
        </motion.h1>
        <motion.p
          className="max-w-2xl text-lg md:text-xl text-slate-300 mb-8"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.3, duration: 0.8 }}
        >
          Accense.ai protects your app by understanding the <em>intent</em> behind every request — not just the identity.
          Stop attacks before they happen with adaptive, AI-powered access control.
        </motion.p>
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.6, duration: 0.8 }}
        >
          <a
            href="https://forms.gle/sfrzmi76KgnLGynAA"
            target="_blank"
            rel="noopener noreferrer"
          >
            <Button>Request Early Access</Button>
          </a>
        </motion.div>
      </section>

      {/* Problem Section */}
      <section className="bg-slate-900 py-20 px-6 border-t border-slate-800">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">
            Traditional Security Falls Short
          </h2>
          <p className="text-slate-400 text-lg">
            Authentication alone isn’t enough. Accense.ai continuously analyzes user behavior and intent, detecting compromised sessions, insider threats, and automation in real time.
          </p>
        </div>
      </section>

      {/* How It Works */}
      <section className="bg-slate-900 py-20 px-6">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold text-center mb-12">How It Works</h2>
          <div className="grid md:grid-cols-3 gap-10">
            {[
              {
                title: "Seamless Integration",
                desc: "Deploy Accense as a reverse proxy or gateway layer — no major refactor needed.",
              },
              {
                title: "Real-Time Intent Analysis",
                desc: "Each request is evaluated in context: user history, geo signals, device ID, and behavioral patterns.",
              },
              {
                title: "Dynamic Response",
                desc: "Trigger block, challenge, or extra auth based on risk scoring — before the threat spreads.",
              },
            ].map((step, idx) => (
              <motion.div
                key={idx}
                className="bg-slate-800 p-6 rounded-xl shadow-lg"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: idx * 0.2, duration: 0.6 }}
                viewport={{ once: true }}
              >
                <h3 className="text-xl font-semibold mb-3 text-white">{step.title}</h3>
                <p className="text-slate-300">{step.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="bg-slate-900 py-20 px-6">
        <div className="max-w-6xl mx-auto grid md:grid-cols-3 gap-12">
          {[
            {
              title: "Intent-Aware",
              desc: "Understands why a user is doing something — not just who they are.",
            },
            {
              title: "AI-Powered",
              desc: "Learns from traffic to detect anomalies, automation, and session misuse.",
            },
            {
              title: "Plug-and-Play",
              desc: "Drop-in reverse proxy or SDK — no app rebuild required.",
            },
          ].map((feature, idx) => (
            <motion.div
              key={idx}
              className="bg-slate-800 p-8 rounded-2xl shadow-lg"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ delay: idx * 0.2, duration: 0.6 }}
              viewport={{ once: true }}
            >
              <h3 className="text-2xl font-semibold mb-4 text-white">{feature.title}</h3>
              <p className="text-slate-300">{feature.desc}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Integrations */}
      <section className="bg-slate-950 py-20 px-6 border-t border-slate-800">
        <div className="max-w-5xl mx-auto text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">Works Seamlessly With</h2>
          <p className="text-slate-400 text-lg mb-8">
            Accense.ai integrates easily with your stack — wherever you deploy.
          </p>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 text-sm text-slate-300">
            <div>Envoy</div><div>NGINX</div><div>Node.js</div><div>Django</div>
            <div>Go</div><div>AWS</div><div>GCP</div><div>Azure</div>
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section className="bg-slate-900 py-20 px-6">
        <div className="max-w-6xl mx-auto text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">Simple, Transparent Pricing</h2>
          <p className="text-slate-400 text-lg mb-12">No surprise fees. Start free, scale as you grow.</p>
          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                plan: "Starter",
                price: "$0/mo",
                features: ["Up to 10k requests/day", "Email support", "Core features"]
              },
              {
                plan: "Pro",
                price: "$49/mo",
                features: ["Up to 1M requests/day", "Priority support", "Advanced risk insights"]
              },
              {
                plan: "Enterprise",
                price: "Custom",
                features: ["Unlimited usage", "Dedicated support", "Custom integrations"]
              }
            ].map((tier, idx) => (
              <motion.div
                key={idx}
                className="bg-slate-800 p-6 rounded-xl shadow-md"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: idx * 0.2, duration: 0.6 }}
                viewport={{ once: true }}
              >
                <h3 className="text-2xl font-semibold text-white mb-2">{tier.plan}</h3>
                <p className="text-xl font-bold text-white mb-4">{tier.price}</p>
                <ul className="text-slate-300 space-y-2">
                  {tier.features.map((feat, i) => (
                    <li key={i}>• {feat}</li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      {/*
      <section className="bg-slate-950 py-20 px-6 border-t border-slate-800">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-12">What Early Users Are Saying</h2>
          <div className="space-y-12">
            {[
              {
                quote: "Accense gave us visibility into API abuse we didn’t know existed. Setup was painless.",
                name: "Sophie W.",
                title: "CTO, DevInfra Inc."
              },
              {
                quote: "Our team caught a compromised account in production thanks to their real-time intent detection.",
                name: "James L.",
                title: "Head of Security, CloudDash"
              }
            ].map((testimonial, idx) => (
              <motion.div
                key={idx}
                className="bg-slate-900 p-8 rounded-xl shadow-md"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: idx * 0.3, duration: 0.6 }}
                viewport={{ once: true }}
              >
                <p className="text-lg italic text-slate-300 mb-4">"{testimonial.quote}"</p>
                <p className="font-semibold text-white">{testimonial.name}</p>
                <p className="text-sm text-slate-500">{testimonial.title}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
      */}

      {/* Final CTA */}
      <section className="py-20 px-6 bg-gradient-to-br from-slate-950 to-slate-900 text-center">
        <h2 className="text-3xl md:text-4xl font-bold mb-4 text-white">Join the Beta</h2>
        <p className="text-slate-300 max-w-xl mx-auto mb-8">
          We’re currently onboarding early partners. Get early access, help shape the roadmap, and secure your stack with next-gen access control.
        </p>
        <a
          href="https://forms.gle/sfrzmi76KgnLGynAA"
          target="_blank"
          rel="noopener noreferrer"
        >
          <Button>Request Access</Button>
        </a>
      </section>

      {/* Footer */}
      <footer className="text-center py-12 text-sm text-slate-500 bg-black border-t border-slate-800">
        <p className="mb-1">Backed by builders of identity systems at Uber, PayPal, and Intuit</p>
        <p>&copy; 2025 accense.ai</p>
      </footer>
    </div>
  );
};

export default LandingPage;
